"use client";

import { Search, ChartBar as BarChart3, ShieldCheck, TrendingUp } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "AUDIT",
  Icon: Search,
  description: "We conduct a deep-dive analysis of your current OTA performance, identifying hidden revenue leaks and parity discrepancies across all channels."
}, {
  number: "02",
  title: "ANALYZE",
  Icon: BarChart3,
  description: "Our team evaluates your tech stack and booking data to pinpoint inefficiencies, ensuring your pricing strategy aligns with market demand."
}, {
  number: "03",
  title: "SECURE",
  Icon: ShieldCheck,
  description: "We implement robust rate parity controls and listing optimizations to protect your brand integrity and maximize direct booking potential."
}, {
  number: "04",
  title: "GROW",
  Icon: TrendingUp,
  description: "We execute data-driven revenue strategies that scale your profitability, turning every guest interaction into a high-value booking opportunity."
}];
export default function LeakageResolutionFramework() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="leakageresolutionframework" className="bg-slate-50 text-slate-900 relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-slate-900/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE FRAMEWORK
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-blue-900" />
              <span className="font-default text-xs font-medium tracking-[0.25em] uppercase text-blue-900">
                Revenue Strategy
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-[64px]">
              Data-driven precision
              <br />
              <span className="text-slate-900/70">
                for maximum hotel yield.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-slate-900/75 md:text-xl">
              At HotelRevBaBa, we eliminate revenue leakage through systematic optimization. We manage OTA listings and tech stacks with a focus on high-conversion results and sustainable growth.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-slate-900/60">
              No guesswork. No missed opportunities. Only the rigorous application of revenue management expertise to secure your property's financial future.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-blue-900" />
                        <span className="h-px w-1 bg-blue-900/60" />
                        <span className="h-px w-0.5 bg-blue-900/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-slate-200 bg-white backdrop-blur-xl shadow-md transition-all duration-500 group-hover:-translate-y-1 group-hover:border-blue-900 group-hover:shadow-lg">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-default text-[120px] font-extrabold leading-none tracking-tighter text-slate-900/10 transition-colors duration-[400ms] group-hover:text-blue-900" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-slate-900/80 transition-colors duration-300 group-hover:text-blue-900" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-slate-900/30 transition-all duration-500 group-hover:w-16 group-hover:bg-blue-900" />
                        <h3 className="font-default text-2xl font-bold tracking-[0.15em] uppercase text-slate-900">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-slate-900/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-900 to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-slate-900/50">
              Four pillars &mdash; one goal
            </p>
            <p className="font-default italic text-sm text-slate-900/60">
              Optimized for revenue. Always.
            </p>
          </div>
        </div>
      </div>
    </section>;
}